import { toast } from 'react-toastify';
import { FailToastContainer, RefreshToastContainer } from 'components/Toast/ToastContainer';
import { CLIENT_CAPABILITIES_REFRESH_MESSAGE, GENERIC_TOAST_ERROR_MESSAGE, HTTPStatusCode } from 'config/const';
import AuthService from 'lib/AuthService';
import Utils from 'lib/Utils';
import { IApiQueryParam } from 'types';
import { calculatorCapabilityHeaders, CLIENT_CAPABILITIES_TOAST_ID, findClientCapabilitiesError } from './clientCapabilities';

export interface IApiQueryOptions {
  limit?: number;
  offset?: number;
  q?: IApiQueryParam[];
  sort?: string;
  includeDismissed?: boolean;
  addExisting?: boolean;
  meta?: unknown;
}

export class ApiResponse<T> {
  public statusCode: number;
  public body: T;

  constructor(statusCode: number, body: T) {
    this.statusCode = statusCode;
    this.body = body;
  }
}

export class ApiError extends Error {
  public statusCode: string;
  public message: string;
  public body: any;

  constructor(statusCode: number, message: string, body: any) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode.toString();
    this.message = message;
    this.body = body;
  }
}

export class ApiSystemError extends Error {
  public statusCode: number;
  public message: string;
  public body: any;

  constructor(statusCode: number, message: string, body: any) {
    super(message);
    this.name = 'ApiSystemError';
    this.statusCode = statusCode;
    this.message = message;
    this.body = body;
  }
}

export class ApiClient {
  public static get = async <T>(path: string, options?: IApiQueryOptions): Promise<ApiResponse<T>> =>
    fetch(ApiClient.buildPath(path, options), ApiClient.getProps())
      .then(ApiClient.handleResponse<T>)
      .then((resp) => resp);

  public static post = async <T>(path: string, body: object, camelCase?: boolean): Promise<ApiResponse<T>> =>
    fetch(path, ApiClient.postProps(body))
      .then((response) => ApiClient.handleResponse<T>(response, camelCase))
      .then((resp) => resp);

  public static put = async <T>(path: string, body: object): Promise<ApiResponse<T>> =>
    fetch(path, ApiClient.putProps(body))
      .then(ApiClient.handleResponse<T>)
      .then((resp) => resp);

  public static delete = async <T>(path: string, body?: object): Promise<ApiResponse<T>> =>
    fetch(path, ApiClient.deleteProps(body))
      .then(ApiClient.handleResponse<T>)
      .then((resp) => resp);

  private static postProps = (body: object): RequestInit => ({
    ...ApiClient.defaultProps(),
    body: JSON.stringify(Utils.keysToSnakeCase(body)),
    method: 'POST',
  });

  private static putProps = (body: object): RequestInit => ({
    ...ApiClient.defaultProps(),
    body: JSON.stringify(Utils.keysToSnakeCase(body)),
    method: 'PUT',
  });

  private static deleteProps = (body?: object): RequestInit => ({
    ...ApiClient.defaultProps(),
    ...(body && { body: JSON.stringify(Utils.keysToSnakeCase(body)) }),
    method: 'DELETE',
  });

  private static getProps = (): RequestInit => ({
    ...ApiClient.defaultProps(),
    method: 'GET',
  });

  private static defaultProps = (): RequestInit => ({
    cache: 'default',
    headers: ApiClient.defaultHeaders(),
    mode: 'cors',
  });

  private static defaultHeaders = (): Headers => {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    return new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Service-Name': 'kickfin-web',
      'x-timestamp': new Date().toISOString(),
      'Time-Zone': timeZone ? timeZone.toLowerCase() : 'utc',
      // Advertise client capabilities on every request. The backend RequestGate only
      // enforces this on the gated calculator routes and tolerantly ignores it elsewhere, so sending
      // it globally keeps a single source of truth instead of wiring it into each gated api call.
      ...calculatorCapabilityHeaders(),
      ...(AuthService.getToken() && { Authorization: `Bearer ${AuthService.getToken()}` }),
    });
  };

  private static handleResponse = async <T>(response: Response, camelCase = true): Promise<ApiResponse<T>> => {
    if (!response.ok && response.status < 500) {
      // A 4xx may carry a non-JSON body (empty, or an HTML error page from a proxy/gateway). Guard
      // the parse so we still throw a well-formed ApiError instead of an unhandled SyntaxError.
      let body: any = null;
      try {
        body = Utils.keysToCamelCase(await response.json());
      } catch {
        body = null;
      }

      // A stale bundle hitting a gated calculator endpoint gets 409 `client_capabilities_required`.
      // Surface the server's "please refresh" message and prompt a reload instead of leaving the
      // caller to render a generic form/error message. We still throw ApiError so the calling saga
      // runs its normal cleanup (re-enabling buttons, clearing loading state, etc.); the matching
      // generic failure toast is suppressed downstream in the ApiClient FAIL saga.
      if (response.status === Number(HTTPStatusCode.CONFLICT)) {
        const capabilityError = findClientCapabilitiesError(body?.errors);
        if (capabilityError) {
          toast(RefreshToastContainer({ message: capabilityError.msg || CLIENT_CAPABILITIES_REFRESH_MESSAGE }), { toastId: CLIENT_CAPABILITIES_TOAST_ID });
        }
      }

      throw new ApiError(response.status, response.statusText, body);
    }
    if (!response.ok && response.status >= 500) {
      toast(FailToastContainer({ message: GENERIC_TOAST_ERROR_MESSAGE }), { toastId: 'unexpected-error' });
      throw new ApiSystemError(response.status, response.statusText, { errors: [{ msg: response.statusText }] });
    }
    const hdrJwt = response.headers.get('x-jwt');
    if (hdrJwt) AuthService.storeToken(hdrJwt);

    const res = response.status === 204 ? null : await response.json();
    return new ApiResponse(response.status, camelCase ? Utils.keysToCamelCase(res) : res);
  };

  private static buildPath = (path: string, options?: IApiQueryOptions) => {
    if (options) return `${path}?${this.buildQuery(options)}`;
    return path;
  };

  private static isSortDesc = (sort: string) => sort[0] === '-';

  private static buildQuery = (options: IApiQueryOptions = {}) => {
    const query: string[] = [];

    if (options.sort) query.push(`sort=${this.isSortDesc(options.sort) ? '-' : ''}${Utils.snakeCaseKey(options.sort)}`);
    if (Number.isFinite(options.limit)) query.push(`limit=${options.limit}`);
    if (options.offset) query.push(`offset=${options.offset}`);
    if (options.includeDismissed) query.push(`include_dismissed=${options.includeDismissed}`);
    if (options.addExisting) query.push(`add_existing=${options.addExisting}`);

    if (options.q) {
      options.q.forEach((item: IApiQueryParam) => {
        query.push(`q[]=${Utils.snakeCaseKey(item.key)}${item.operator}${encodeURIComponent(item.value.toString())}`);
      });
    }

    return query.join('&');
  };
}
